import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import * as Forge from 'node-forge';
import { Observable, Subject, catchError, takeUntil, tap, throwError } from 'rxjs';

import { LOGIN_URL } from '@appCore/constants/api';
import { LoginResponse, TokenData, initialTokenData } from '@appCore/models';
import { TokenField, checkAppAndRole, getTokenNumericPayloadData, getTokenPayloadData } from '@appCore/utilities';
import { RolesService } from './roles.service';
import { StoreService } from './store.service';

const dummyToken =
    'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJodHRwOi8vc2NoZW1hcy54bWxzb2FwLm9yZy93cy8yMDA1LzA1L2lkZW50aXR5L2NsYWltcy9zaWQiOiIyNCIsImh0dHA6Ly9zY2hlbWFzLnhtbHNvYXAub3JnL3dzLzIwMDUvMDUvaWRlbnRpdHkvY2xhaW1zL25hbWUiOiJhZG1pbkB0ZXN0LmVzIiwianRpIjoiMjBhZjJhZDYtYWQ5MS00YWI0LWE3NDItYjYzZDg0MTM0M2JhIiwiaHR0cDovL3NjaGVtYXMubWljcm9zb2Z0LmNvbS93cy8yMDA4LzA2L2lkZW50aXR5L2NsYWltcy9yb2xlIjoiQ29tZXJjaWFsLURpcmVjdG9yYSIsIm5iZiI6MTY5ODkxNTkwNSwiZXhwIjoxNzAxNTQzOTA1LCJpc3MiOiJYTlFtUXN0WVJwdE80c3Z2VXNndDB2RUgzIiwiYXVkIjoiUGlsYUQ5NlJCZnJiWXRYZUVkR2ZyU0FFTCJ9.2wA45NAJ2sVO0_8qd37QSrZ_7whaqOwQWcLNNeOWwAs';

@Injectable({
    providedIn: 'root'
})
export class AuthService implements OnDestroy {
    private publicKey: string = `-----BEGIN PUBLIC KEY-----
    MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQCOwWbh9uIb+2uzxT6VrPeqj2a1
    nOMFFaCz4TizNIJLd823/SK3TxkxlAU/tBC+7rrOIeVsxpEUuvj5UgY5M6P8eW45
    wJf+s5mNUTWuK3C0Mfg5XjVBRtxogJu9lTbJvaf176Mv+9LnU+ZTf8QHHKt8TcK7
    SUnDSaBCdi6ciV3NvwIDAQAB
    -----END PUBLIC KEY-----`;
    private destroy$ = new Subject<void>();

    constructor(private storeService: StoreService, private roleService: RolesService, private http: HttpClient, private router: Router) {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** Checks if the user is authenticated with a not expired token */
    public isAuthenticated(): boolean {
        return this.storeService.isAuthenticated();
    }

    /** Gets the accessToken  */
    public getAccessToken(): string {
        const { accessToken } = this.storeService.getTokenData();
        return accessToken;
    }

    public login(loginData: { email: string; password: string }): Observable<LoginResponse> {
        const { email, password } = loginData;
        const trimmedLoginData = {
            username: this.encryptWithPublicKey(email.trim()).toString(),
            password: this.encryptWithPublicKey(password.trim()).toString()
        };

        return this.http.post<LoginResponse>(LOGIN_URL, trimmedLoginData).pipe(
            tap((response) => {
                this.initialize(response);
            })
        );
    }

    public logout(): void {
        this.storeService.reset();
        this.router.navigateByUrl('/login');
    }

    // PRIVATE

    /** Initializes User and TokenData in AppState */
    private initialize(response: LoginResponse): void {
        const { data: tokenData, succeeded, errors } = response;
        const { accessToken } = tokenData;
        const error = 'Token error (internal)';
        const status = 422;
        if (succeeded && accessToken) {
            try {
                this.getUserInfo(accessToken, tokenData, error, status);
            } catch (e) {
                this.storeService.setTokenData(initialTokenData);
                this.router.navigateByUrl('login');
                throw new HttpErrorResponse({ error, status });
            }
        }

        if (errors && errors.length) {
            console.error(errors);
        }
    }

    private getUserInfo(accessToken: string, tokenData: TokenData, error: string, status: number) {
        const userId = getTokenPayloadData(accessToken, TokenField.UserId);
        const userName = getTokenPayloadData(accessToken, TokenField.UserName);
        const role = getTokenPayloadData(accessToken, TokenField.Role);
        const preferedDepartamentId = getTokenNumericPayloadData(accessToken, TokenField.DepartamentId);
        const authorisation = checkAppAndRole(typeof role === 'string' ? [role] : role);
        this.storeService.setTokenData(tokenData);

        if (!authorisation?.authoirsedUser) {
            error = 'Unauthorised User';
            status = 403;
        }
        this.storeService.setUser(userId, userName, authorisation.role, authorisation.application, preferedDepartamentId);
        this.roleService
            .getPermissions()
            .pipe(
                tap((permissions) => {
                    this.storeService.setPermissions(permissions);
                }),
                catchError((err) => {
                    this.storeService.setPermissions([]);
                    console.error('Error al obtener roles o guardarlos', err);
                    return throwError(() => err);
                }),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    private encryptWithPublicKey(valueToEncrypt: string): string {
        const rsa = Forge.pki.publicKeyFromPem(this.publicKey);
        return window.btoa(rsa.encrypt(valueToEncrypt.toString()));
    }
}
